import { useMemo } from 'react';
import { usdToCredits } from '@/lib/credits/estimate';
import { useCreditRate } from '@/hooks/use-credit-rate';
import { useSubscription, resolveModelPassRemaining } from '@/providers/subscription';
import { applyPassDiscountToCount, resolveDiscountMultiplier } from '@flowmuse/billing/discount-multiplier';

export type EstimatedBilling = {
  /** 折后 credits（实际扣费量）。usdCost 无效时为 null。 */
  credits: number | null;
  /** 折扣前 credits（按 USD 原价换算）。usdCost 无效时为 null。 */
  creditsBeforeDiscount: number | null;
  /** 当前用户在该 model 上的折扣倍数（1 = 不打折）。 */
  discountMultiplier: number;
  passRemaining: number;
  /** 折后次卡消耗数量（已应用模型卡折扣，round 到 2 位小数，与服务端实扣口径一致）。 */
  passCount: number;
  /** 模型卡折扣倍数（1 = 不打折），与积分折扣相互独立。 */
  passDiscountMultiplier: number;
};

export function useEstimatedCredits(usdCost: number | null | undefined, modelId?: string): number | null {
  const { credits } = useEstimatedBilling(usdCost, modelId);
  return credits;
}

export function useEstimatedBilling(
  usdCost: number | null | undefined,
  modelId?: string,
  /** 本次生成的原始（未折扣）次卡消耗数量。 */
  rawPassCount: number = 1,
): EstimatedBilling {
  const { data: creditRate } = useCreditRate();
  const { discountMap, passDiscountMap, modelPassMap } = useSubscription();

  return useMemo(() => {
    const passRemaining = modelId ? resolveModelPassRemaining(modelPassMap, modelId) : 0;
    const multiplier = modelId ? resolveDiscountMultiplier(discountMap, modelId) : 1;
    const passDiscountMultiplier = modelId ? resolveDiscountMultiplier(passDiscountMap, modelId) : 1;
    // 与服务端实扣共用同一取整规则（applyPassDiscountToCount），保证预估 = 实扣。
    const passCount = applyPassDiscountToCount(rawPassCount, passDiscountMultiplier);

    if (usdCost === null || usdCost === undefined || !Number.isFinite(usdCost)) {
      return { credits: null, creditsBeforeDiscount: null, discountMultiplier: multiplier, passRemaining, passCount, passDiscountMultiplier };
    }

    const creditValueUsd = creditRate?.creditValueUsd ?? 0.01;
    if (usdCost <= 0) return { credits: 0, creditsBeforeDiscount: 0, discountMultiplier: multiplier, passRemaining, passCount, passDiscountMultiplier };

    const creditsBeforeDiscount = usdToCredits(usdCost, creditValueUsd);
    const cost = usdCost * multiplier;
    const credits = cost <= 0 ? 0 : usdToCredits(cost, creditValueUsd);

    return { credits, creditsBeforeDiscount, discountMultiplier: multiplier, passRemaining, passCount, passDiscountMultiplier };
  }, [usdCost, creditRate?.creditValueUsd, discountMap, passDiscountMap, modelPassMap, modelId, rawPassCount]);
}
